import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { IconoMenu } from "./MenuModuloIconos";
import { obtenerAccesosRecientes, describirFechaRelativa } from "../../../Helpers/AccesosRecientes";

/**
 * MenuModulo — menú principal de un módulo, agrupado en tarjetas.
 *
 * Componente de presentación reutilizable: NO conoce endpoints ni rutas. Recibe los accesos que ya
 * trae la pantalla (el API los entrega planos y filtrados por permisos) y la definición de grupos,
 * y se encarga de agrupar, buscar y navegar. Sirve igual para Crédito, Cobranza o cualquier otro
 * módulo: solo cambia el arreglo de grupos.
 *
 * Props
 * - accesos: [{ titulo, ruta }] tal cual los regresa MenuUsuarios/AccesosMenus.
 * - grupos: definición de grupos (ver src/Arrays/MenuCreditoArray.js).
 * - idmodulo: se usa para guardar los accesos recientes por módulo.
 * - onAbrir: (acceso) => void. La pantalla decide cómo navega.
 * - cargando: muestra el esqueleto mientras llega el API.
 * - mostrarRecientes: tarjeta de últimos accesos (por defecto activa).
 * - placeholderBusqueda: texto del buscador.
 *
 * Creado: 2026-09-17 — IA (Claude)
 */

const LARGO_MAXIMO_BUSQUEDA = 60;

const GRUPO_OTROS = {
  clave: "__otros__",
  titulo: "Otros accesos",
  descripcion: "Sin clasificar",
  icono: "otros",
  color: "neutro",
};

// Quita acentos conservando la longitud del texto (se normaliza carácter por carácter), para poder
// resaltar la coincidencia sobre el texto original aunque la búsqueda venga sin acentos.
const quitarAcentos = (texto) =>
  String(texto)
    .split("")
    .map((caracter) => caracter.normalize("NFD").replace(/[̀-ͯ]/g, "") || caracter)
    .join("");

const normalizar = (texto) => quitarAcentos(texto || "").toLowerCase();

const normalizarRuta = (ruta) => normalizar(String(ruta || "").trim()).replace(/\/+$/, "");

const MenuModulo = ({
  accesos = [],
  grupos = [],
  idmodulo,
  onAbrir,
  cargando = false,
  mostrarRecientes = true,
  placeholderBusqueda = "Buscar acceso…",
}) => {
  const [busqueda, setBusqueda] = useState("");
  const inputRef = useRef(null);

  // Los recientes se leen una sola vez al montar: al abrir un acceso se sale de la pantalla.
  const [recientes] = useState(() =>
    mostrarRecientes && idmodulo !== undefined ? obtenerAccesosRecientes(idmodulo) : [],
  );

  // Índice ruta → grupo/icono, construido una sola vez a partir de la definición de grupos.
  const indicePorRuta = useMemo(() => {
    const mapa = new Map();
    grupos.forEach((grupo, ordenGrupo) => {
      (grupo.accesos || []).forEach((acceso, posicion) => {
        mapa.set(normalizarRuta(acceso.ruta), {
          clave: grupo.clave,
          icono: acceso.icono,
          etiqueta: acceso.etiqueta,
          ordenGrupo,
          posicion,
        });
      });
    });
    return mapa;
  }, [grupos]);

  // Acomoda los accesos del API dentro de sus grupos. Un acceso cuya ruta no esté clasificada cae
  // en "Otros accesos": así un menú nuevo del backend se sigue viendo aunque nadie actualice el
  // arreglo. Un grupo sin accesos (por permisos) no se dibuja.
  const gruposConAccesos = useMemo(() => {
    const porGrupo = new Map();
    const sinClasificar = [];

    (accesos || []).forEach((acceso) => {
      if (!acceso || typeof acceso.ruta !== "string") return;

      const info = indicePorRuta.get(normalizarRuta(acceso.ruta));
      const tituloApi = String(acceso.titulo || "").trim();
      const item = {
        // El API manda los títulos en MAYÚSCULAS y sin acentos: si el grupo define una etiqueta
        // legible se usa esa, y si no, se respeta tal cual lo que mandó el backend.
        titulo: (info && info.etiqueta) || tituloApi,
        ruta: acceso.ruta,
        icono: info ? info.icono : "generico",
        posicion: info ? info.posicion : Number.MAX_SAFE_INTEGER,
      };

      if (!info) {
        sinClasificar.push(item);
        return;
      }
      if (!porGrupo.has(info.clave)) porGrupo.set(info.clave, []);
      porGrupo.get(info.clave).push(item);
    });

    const resultado = grupos
      .map((grupo) => ({
        ...grupo,
        items: (porGrupo.get(grupo.clave) || []).sort((a, b) => a.posicion - b.posicion),
      }))
      .filter((grupo) => grupo.items.length > 0);

    if (sinClasificar.length > 0) resultado.push({ ...GRUPO_OTROS, items: sinClasificar });

    return resultado;
  }, [accesos, grupos, indicePorRuta]);

  const consulta = normalizar(busqueda.trim());

  const gruposVisibles = useMemo(() => {
    if (!consulta) return gruposConAccesos;

    return gruposConAccesos
      .map((grupo) => {
        // Si el nombre del grupo coincide se conservan todos sus accesos.
        const coincideGrupo = normalizar(grupo.titulo).includes(consulta);
        return {
          ...grupo,
          items: coincideGrupo
            ? grupo.items
            : grupo.items.filter((item) => normalizar(item.titulo).includes(consulta)),
        };
      })
      .filter((grupo) => grupo.items.length > 0);
  }, [gruposConAccesos, consulta]);

  const totalAccesos = useMemo(
    () => gruposConAccesos.reduce((suma, grupo) => suma + grupo.items.length, 0),
    [gruposConAccesos],
  );

  const totalVisibles = useMemo(
    () => gruposVisibles.reduce((suma, grupo) => suma + grupo.items.length, 0),
    [gruposVisibles],
  );

  // Atajo "/" para enfocar el buscador, ignorado mientras se escribe en otro campo.
  useEffect(() => {
    const onTecla = (evento) => {
      if (evento.key !== "/" || evento.ctrlKey || evento.altKey || evento.metaKey) return;

      const destino = evento.target;
      const etiqueta = (destino && destino.tagName) || "";
      if (etiqueta === "INPUT" || etiqueta === "TEXTAREA" || (destino && destino.isContentEditable)) return;

      evento.preventDefault();
      if (inputRef.current) inputRef.current.focus();
    };

    document.addEventListener("keydown", onTecla);
    return () => document.removeEventListener("keydown", onTecla);
  }, []);

  // Mientras se busca, la tarjeta de recientes se oculta: estorbaría entre los resultados.
  const mostrarTarjetaRecientes = mostrarRecientes && !consulta;

  const abrir = (acceso) => {
    if (typeof onAbrir === "function") onAbrir(acceso);
  };

  const onTeclaBuscador = (evento) => {
    if (evento.key === "Escape") {
      setBusqueda("");
      return;
    }
    // Enter abre el primer resultado de la lista filtrada.
    if (evento.key === "Enter") {
      const primerGrupo = gruposVisibles[0];
      const primerAcceso = primerGrupo && primerGrupo.items[0];
      if (primerAcceso) abrir(primerAcceso);
    }
  };

  // Resalta la parte del título que coincide con la búsqueda, sin inyectar HTML.
  const resaltar = (texto) => {
    if (!consulta) return texto;

    const posicion = normalizar(texto).indexOf(consulta);
    if (posicion === -1) return texto;

    return (
      <>
        {texto.slice(0, posicion)}
        <mark className="mmod-marca">{texto.slice(posicion, posicion + consulta.length)}</mark>
        {texto.slice(posicion + consulta.length)}
      </>
    );
  };

  const renderAcceso = (item) => (
    <button
      key={item.ruta}
      type="button"
      className="mmod-acceso"
      onClick={() => abrir(item)}
      title={item.titulo}
    >
      <span className="mmod-acceso-ico">
        <IconoMenu clave={item.icono} />
      </span>
      <span className="mmod-acceso-txt">{resaltar(item.titulo)}</span>
      <ChevronRightIcon className="mmod-acceso-chev" />
    </button>
  );

  // Tarjeta de accesos recientes. Va PRIMERO en la retícula cuando tiene contenido: es el acceso
  // rápido y tiene que quedar a la mano conforme el menú crezca. Si todavía no hay nada que
  // mostrar se manda al final, para no ocupar el mejor lugar con una tarjeta vacía.
  const renderTarjetaRecientes = () => (
        <section className="mmod-card mmod-card-recientes">
          <div className="mmod-card-head">
            <span className="mmod-card-ico mmod-color-neutro">
              <IconoMenu clave="recientes" />
            </span>
            <div className="mmod-card-textos">
              <h3 className="mmod-card-titulo">Tus accesos recientes</h3>
              <p className="mmod-card-sub">Últimos que abriste</p>
            </div>
          </div>
          <div className="mmod-sep" />

          {recientes.length > 0 ? (
            <div className="mmod-lista">
              {recientes.map((reciente) => {
                // La etiqueta se resuelve al pintar, no se confía en el título guardado: así un
                // reciente guardado antes de cambiar el nombre de un acceso se ve actualizado.
                const info = indicePorRuta.get(normalizarRuta(reciente.ruta)) || {};
                const etiqueta = info.etiqueta || reciente.titulo;

                return (
                  <button
                    key={reciente.ruta}
                    type="button"
                    className="mmod-acceso mmod-acceso-reciente"
                    onClick={() => abrir({ ...reciente, titulo: etiqueta })}
                    title={etiqueta}
                  >
                    <span className="mmod-acceso-ico">
                      <IconoMenu clave={info.icono} />
                    </span>
                    <span className="mmod-acceso-txt">{etiqueta}</span>
                    <span className="mmod-acceso-fecha">{describirFechaRelativa(reciente.fecha)}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="mmod-recientes-vacio">
              Aquí aparecerán los últimos accesos que abras desde este menú.
            </p>
          )}

          <p className="mmod-nota">Se guardan en este equipo, no en el servidor.</p>
        </section>
  );

  const renderEsqueleto = () => (
    <div className="mmod-grid">
      {[0, 1, 2, 3, 4, 5].map((indice) => (
        <div className="mmod-card mmod-card-esqueleto" key={indice}>
          <div className="mmod-card-head">
            <span className="mmod-esq mmod-esq-ico" />
            <div className="mmod-card-textos">
              <span className="mmod-esq mmod-esq-titulo" />
              <span className="mmod-esq mmod-esq-sub" />
            </div>
          </div>
          <div className="mmod-sep" />
          <div className="mmod-lista">
            <span className="mmod-esq mmod-esq-fila" />
            <span className="mmod-esq mmod-esq-fila" />
            <span className="mmod-esq mmod-esq-fila" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mmod-cuerpo">
      <div className="mmod-barra">
        <div className="mmod-buscador">
          <SearchIcon className="mmod-buscador-lupa" />
          <label className="mmod-oculto" htmlFor={`mmod-busqueda-${idmodulo}`}>
            Buscar acceso
          </label>
          <input
            id={`mmod-busqueda-${idmodulo}`}
            ref={inputRef}
            className="mmod-buscador-input"
            type="text"
            autoComplete="off"
            maxLength={LARGO_MAXIMO_BUSQUEDA}
            placeholder={placeholderBusqueda}
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
            onKeyDown={onTeclaBuscador}
          />
          {busqueda ? (
            <button
              type="button"
              className="mmod-buscador-limpiar"
              aria-label="Limpiar búsqueda"
              onClick={() => {
                setBusqueda("");
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              <CloseIcon />
            </button>
          ) : (
            <span className="mmod-buscador-atajo" aria-hidden="true">/</span>
          )}
        </div>

        <div className="mmod-conteo">
          <span className="mmod-conteo-dato">{gruposVisibles.length}</span>
          {gruposVisibles.length === 1 ? " grupo" : " grupos"}
          <span className="mmod-conteo-div" />
          <span className="mmod-conteo-dato">{consulta ? totalVisibles : totalAccesos}</span>
          {consulta ? ` de ${totalAccesos} accesos` : totalAccesos === 1 ? " acceso" : " accesos"}
        </div>
      </div>

      {cargando && renderEsqueleto()}

      {!cargando && totalAccesos === 0 && (
        <div className="mmod-vacio">
          <SearchOffIcon />
          <p className="mmod-vacio-titulo">No hay accesos habilitados para tu perfil</p>
          <p className="mmod-vacio-texto">Si necesitas alguno, solicítalo a Gestión TI.</p>
        </div>
      )}

      {!cargando && totalAccesos > 0 && gruposVisibles.length === 0 && (
        <div className="mmod-vacio">
          <SearchOffIcon />
          <p className="mmod-vacio-titulo">Sin resultados para «{busqueda.trim()}»</p>
          <button type="button" className="mmod-vacio-boton" onClick={() => setBusqueda("")}>
            Limpiar búsqueda
          </button>
        </div>
      )}

      {!cargando && gruposVisibles.length > 0 && (
        <div className="mmod-grid">
          {mostrarTarjetaRecientes && recientes.length > 0 && renderTarjetaRecientes()}

          {gruposVisibles.map((grupo) => (
            <section className="mmod-card" key={grupo.clave}>
              <div className="mmod-card-head">
                <span className={`mmod-card-ico mmod-color-${grupo.color || "neutro"}`}>
                  <IconoMenu clave={grupo.icono} />
                </span>
                <div className="mmod-card-textos">
                  <h3 className="mmod-card-titulo">{grupo.titulo}</h3>
                  {grupo.descripcion && <p className="mmod-card-sub">{grupo.descripcion}</p>}
                </div>
                <span className={`mmod-card-pill mmod-color-${grupo.color || "neutro"}`}>
                  {grupo.items.length}
                </span>
              </div>
              <div className="mmod-sep" />
              <div className="mmod-lista">{grupo.items.map(renderAcceso)}</div>
            </section>
          ))}

          {mostrarTarjetaRecientes && recientes.length === 0 && renderTarjetaRecientes()}
        </div>
      )}
    </div>
  );
};

export default MenuModulo;
